/**
 * Dashboard & Analytics API Service
 *
 * Handles all dashboard and analytics API calls:
 * - Dashboard details (for landlords and sub-users)
 * - Home transactions
 * - Analytics data
 */

import {post, isSubUser, getSubUserId, getLandlordId} from './client';

export const dashboardApi = {
    /**
     * Get dashboard details - automatically routes to correct endpoint based on user type
     *
     * For Landlords:
     *   - Endpoint: POST /pegasus/visionary/dashboard/get/details
     *   - Request: { "landlord_id": 249 }
     *
     * For Sub-Users:
     *   - Endpoint: POST /pegasus/visionary/userManagement/dashboard
     *   - Request: { "sub_user_id": 2, "month": 10, "year": 2025 }
     *   - Note: month and year are optional and default to current month/year
     *
     * @param {Object} dashboardData - Dashboard request data
     * @param {Object} [dashboardData.userData] - User details (optional, for backward compatibility)
     * @param {number} [dashboardData.month] - Optional month filter (1-12)
     * @param {number} [dashboardData.year] - Optional year filter
     * @param {number} [dashboardData.landlord_id] - Landlord ID (optional, auto-detected)
     * @param {number} [dashboardData.sub_user_id] - Sub-user ID (optional, auto-detected)
     * @returns {Promise<Object>} Dashboard details with identical structure for both user types
     */
    getDetails: async (dashboardData = {}) => {

        const accessToken = dashboardData.access_token;

        // Check if dashboardData contains sub_user_id (direct from login response)
        // Sub-user login response structure: { sub_user_id, landlord_id, email, ... }

        try {
            const hasSubUserId = dashboardData.sub_user_id || dashboardData.sub_user_id;

            // Check session storage for user type
            // const userIsSubUser = isSubUser();
            const userIsSubUser = true;

            if (hasSubUserId || userIsSubUser) {
                // Sub-user endpoint
                // Try to get sub_user_id from multiple sources:
                // 1. Direct property in dashboardData (from login response)
                // 2. Nested in userData object
                // 3. From session storage (for subsequent calls)
                const subUserId = dashboardData.sub_user_id ||
                    dashboardData.sub_user_id ||
                    getSubUserId();

                if (!subUserId) {
                    throw new Error('Sub-user ID not found in session');
                }

                const requestBody = {
                    sub_user_id: subUserId,
                };

                // Add optional month and year if provided
                if (dashboardData.month) {
                    requestBody.month = dashboardData.month;
                }
                if (dashboardData.year) {
                    requestBody.year = dashboardData.year;
                }

                // Send request with custom access token header if provided
                return post('pegasus/visionary/userManagement/dashboard', requestBody, {
                    headers: accessToken ? {token: accessToken} : {}
                });
            } else {

                console.log("==============================================================================")
                console.log("LANDLORD LOGGED IN")
                console.log("==============================================================================")
                // Landlord endpoint (original behavior)
                const landlordId = getLandlordId();

                // Use existing dashboardData or construct from landlord_id
                const requestBody = dashboardData.landlord_id
                    ? {landlord_id: dashboardData.landlord_id}
                    : dashboardData.userData
                        ? dashboardData
                        : {landlord_id: landlordId};

                // Send request with custom access token header if provided
                return post('pegasus/visionary/dashboard/get/details', requestBody, {
                    headers: accessToken ? {token: accessToken} : {}
                });
            }
        } catch (e) {
            // Landlord endpoint (original behavior)
            const landlordId = getLandlordId();

            // Use existing dashboardData or construct from landlord_id
            const requestBody = dashboardData.landlord_id
                ? {landlord_id: dashboardData.landlord_id}
                : dashboardData.userData
                    ? dashboardData
                    : {landlord_id: landlordId};

            // Send request with custom access token header if provided
            return post('pegasus/visionary/dashboard/get/details', requestBody, {
                headers: accessToken ? {token: accessToken} : {}
            });

        }
    },

    /**
     * Get home page transactions
     * @param {string} landlordId - Landlord ID
     * @returns {Promise<Object>} Recent transactions
     */
    getHomeTransactions: async (landlordId) => {
        return post('pegasus/visionary/dashboard/get/home/transactions', {
            landlord_id: landlordId,
        });
    },
};

/**
 * Analytics API Service
 */
export const analyticsApi = {
    /**
     * Get analytics data
     * @param {Object} analyticsData
     * @param {string} analyticsData.landlord_id - Landlord ID
     * @param {number} analyticsData.year - Year for analytics
     * @returns {Promise<Object>} Analytics data
     */
    getAnalytics: async (analyticsData) => {
        return post('pegasus/visionary/analytics/', analyticsData);
    },
};

export default dashboardApi;
